import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useRequestSupplementaryStore } from '@lib/store';
import { useAccountContext } from '@shared/hooks';
import { MobileHeader, Success } from '@widgets';
import { Button, DateInput, BaseSelect, TextInput, RadioInput, Spinner } from '@shared/widgets';
import { countries } from './countries';

export enum GenderEnum {
  Male = 'Male',
  Female = 'Female',
}

interface IRequestSupplementaryForm {
  name: string;
  surname: string;
  birthdate: string;
  nationality: string;
  passport: string;
  gender?: GenderEnum;
  address: string;
  password: string;
  occupation: string;
}
const DEFAULT_VALUES: IRequestSupplementaryForm = {
  name: '',
  surname: '',
  birthdate: '',
  nationality: '',
  passport: '',
  gender: undefined,
  address: '',
  password: '',
  occupation: '',
};

export const RequestSupplementary = () => {
  const requestSupplementaryStore = useRequestSupplementaryStore();

  const { account } = useAccountContext();
  const requestSupplementaryForm = useForm<IRequestSupplementaryForm>({ defaultValues: DEFAULT_VALUES });
  const { t } = useTranslation('card');

  useEffect(() => {
    return () => requestSupplementaryStore.clear();
  }, []);

  const onSubmit = (data: IRequestSupplementaryForm) => {
    if (!account) return;
    requestSupplementaryStore.post({
      accountId: account.id,
      name: data.name,
      surname: data.surname,
      dateOfBirth: data.birthdate,
      nationality: data.nationality,
      passport: data.passport,
      gender: data.gender!,
      address: data.address,
      password: data.password,
      occupation: data.occupation,
    });
  };

  const countryOptions = useMemo(() => countries.map((country) => ({ value: country, label: country })), []);
  const genderOptions = useMemo(
    () => [
      { label: t('requestSupplementaryCard.placeholders.genders.male'), value: GenderEnum.Male },
      { label: t('requestSupplementaryCard.placeholders.genders.female'), value: GenderEnum.Female },
    ],
    [t],
  );

  if (!account?.id) return null;
  return (
    <>
      <MobileHeader />
      <form className='relative z-1 p-10 font-medium text-base' noValidate onSubmit={requestSupplementaryForm.handleSubmit(onSubmit)}>
        {requestSupplementaryStore.loading && <Spinner position dark />}
        {requestSupplementaryStore.success && <Success text={t('requestSupplementaryCard.success')} />}
        <p className=' text-xl font-normal max-w-[538px] mr-auto mb-10'>{t('requestSupplementaryCard.title')}</p>
        <ul className='flex flex-row flex-wrap list-none p-0 mr-[-38px] max-[618px]:flex-col'>
          <li className='mr-[38px] flex-1 min-w-[250px]'>
            <Controller
              name='name'
              control={requestSupplementaryForm.control}
              rules={{ required: t('errors.form.requiredField', { ns: 'common', field: t('placeholders.name', { ns: 'common' }) }) }}
              render={({ field, fieldState: { error } }) => (
                <TextInput
                  {...field}
                  formStyle
                  caption={`${t('placeholders.name', { ns: 'common' })}:`}
                  name='Name'
                  required
                  error={error?.message}
                />
              )}
            />
            <Controller
              name='surname'
              control={requestSupplementaryForm.control}
              rules={{ required: t('errors.form.requiredField', { ns: 'common', field: t('placeholders.surname', { ns: 'common' }) }) }}
              render={({ field, fieldState: { error } }) => (
                <TextInput
                  {...field}
                  formStyle
                  caption={`${t('placeholders.surname', { ns: 'common' })}:`}
                  name='Surname'
                  required
                  error={error?.message}
                />
              )}
            />
            <Controller
              name='birthdate'
              control={requestSupplementaryForm.control}
              rules={{ required: t('errors.form.requiredField', { ns: 'common', field: t('placeholders.birthdate', { ns: 'common' }) }) }}
              render={({ field, fieldState: { error } }) => (
                <DateInput {...field} formStyle caption={`${t('placeholders.dateOfBirth', { ns: 'common' })}:`} required error={error?.message} />
              )}
            />
            <Controller
              name='nationality'
              control={requestSupplementaryForm.control}
              rules={{ required: t('errors.form.requiredField', { ns: 'common', field: t('placeholders.nationality', { ns: 'common' }) }) }}
              render={({ field, fieldState: { error } }) => (
                <BaseSelect
                  {...field}
                  className='w-full mt-6'
                  field='nationality'
                  caption={`${t('placeholders.nationality', { ns: 'common' })}:`}
                  error={error?.message}
                  values={countryOptions}
                  required
                  formStyle
                />
              )}
            />
            <Controller
              name='password'
              control={requestSupplementaryForm.control}
              rules={{ required: t('errors.form.requiredField', { ns: 'common', field: t('placeholders.password', { ns: 'common' }) }) }}
              render={({ field, fieldState: { error } }) => (
                <TextInput
                  {...field}
                  formStyle
                  caption={`${t('placeholders.password', { ns: 'common' })}:`}
                  name='password'
                  type='password'
                  required
                  error={error?.message}
                />
              )}
            />
          </li>
          <li className='mr-[38px] flex-1 min-w-[250px]'>
            <Controller
              name='passport'
              control={requestSupplementaryForm.control}
              rules={{ required: t('errors.form.requiredField', { ns: 'common', field: t('placeholders.passportNumber', { ns: 'common' }) }) }}
              render={({ field, fieldState: { error } }) => (
                <TextInput
                  {...field}
                  inputClassName='!w-[200px]'
                  formStyle
                  caption={`${t('placeholders.passportNumber', { ns: 'common' })}:`}
                  name='passport'
                  required
                  error={error?.message}
                />
              )}
            />
            <Controller
              name='gender'
              control={requestSupplementaryForm.control}
              rules={{ required: t('errors.form.requiredField', { ns: 'common', field: t('placeholders.gender', { ns: 'common' }) }) }}
              render={({ field, fieldState: { error } }) => (
                <RadioInput
                  {...field}
                  formStyle
                  caption={`${t('placeholders.gender', { ns: 'common' })}:`}
                  field='gender'
                  values={genderOptions}
                  required
                  error={error?.message}
                />
              )}
            />
            <Controller
              name='address'
              control={requestSupplementaryForm.control}
              rules={{ required: t('errors.form.requiredField', { ns: 'common', field: t('placeholders.residentialAddress', { ns: 'common' }) }) }}
              render={({ field, fieldState: { error } }) => (
                <TextInput
                  {...field}
                  formStyle
                  caption={`${t('placeholders.residentialAddress', { ns: 'common' })}:`}
                  name='address'
                  required
                  error={error?.message}
                />
              )}
            />
            <Controller
              name='occupation'
              control={requestSupplementaryForm.control}
              rules={{ required: t('errors.form.requiredField', { ns: 'common', field: t('placeholders.occupation', { ns: 'common' }) }) }}
              render={({ field, fieldState: { error } }) => (
                <TextInput
                  {...field}
                  formStyle
                  caption={`${t('placeholders.occupation', { ns: 'common' })}:`}
                  name='occupation'
                  required
                  error={error?.message}
                />
              )}
            />
          </li>
        </ul>
        <div>{t('requestSupplementaryCard.hint')}</div>
        <Button
          className='min-w-[140px] tracking-[0] px-4 ml-auto'
          view='secondary'
          caption={t('buttons.continue', { ns: 'common' })}
          type='submit'
        />
      </form>
    </>
  );
};
